import { ImagePlus, Settings2 } from "@tamagui/lucide-icons";
import * as DocumentPicker from "expo-document-picker";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { observer } from "mobx-react-lite";
import React from "react";
import { type ModalProps, Pressable } from "react-native";
import { Image, XStack, YStack } from "tamagui";
import { Button } from "../../ui/Button";
import { log } from "../../ui/log";
import { Modal } from "../../ui/Modal";
import { TipsText } from "../../ui/TipsText";
import { Title } from "../../ui/Title";
import { storage } from "../firebaseInit";
import { trackEvent } from "../trackEvent";

import { usePerformance } from "../usePerformance";
import { getImageInfo, isRunningInApp } from "../utils";
import { CropImage } from "./CropImage";
import { availableCovers } from "./covers";

const CoverModal: React.FC<
  ModalProps & {
    uploadButton?: JSX.Element;
    onClose: () => void;
    onSelect: (imgUrl: string) => void;
  }
> = observer(({ uploadButton, onSelect, onClose, ...props }) => {
  const selectedCover = React.useCallback(
    (imgUrl: string) => {
      onSelect(imgUrl);
      onClose();
    },
    [onSelect, onClose],
  );

  return (
    <Modal
      modalId="upload_cover"
      onClose={onClose}
      {...props}
      header={
        <YStack gap="$medium">
          <XStack gap="$medium" alignItems="center">
            <Settings2 />
            <Title>Set a cover image</Title>
          </XStack>
          <TipsText
            text={"Select a theme you feel is perfect or upload your own"}
          />
        </YStack>
      }
      footer={
        !isRunningInApp() && !!uploadButton ? (
          <YStack gap="$medium">{uploadButton}</YStack>
        ) : undefined
      }
    >
      <YStack gap={1} flexWrap="wrap" width={"100%"} cursor="pointer">
        {availableCovers.map((cover, i) => (
          <Pressable
            key={i}
            onPress={() => {
              selectedCover(cover.url);
            }}
            style={{
              aspectRatio: 3 / 1,
              width: "100%",
            }}
          >
            <Image
              source={{ uri: cover.url }}
              style={{ aspectRatio: 3 / 1 }}
              objectFit="cover"
            />
          </Pressable>
        ))}
      </YStack>
    </Modal>
  );
});

function readFile(file: File): Promise<string | ArrayBuffer | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

export function useUploadCover(
  uploadStateCallback: (isUploading: boolean) => void,
  onSelect: (coverUrl: string) => Promise<void>,
  uid?: string,
) {
  const performanceMeasure = usePerformance("UPLOAD_COVER");
  const [imageSrc, setImageSrc] = React.useState<string | null>(null);

  const [showCoverModal, setShowCoverModal] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);

  const uploadFile = React.useCallback(
    async (uploadCroppedFile: Blob) => {
      const storagePath = "covers";

      performanceMeasure.start();
      uploadStateCallback(true);
      setIsUploading(true);

      try {
        // Create a reference to 'images/mountains.jpg'
        const storageRef = ref(storage, `${storagePath}/${uid}`);

        const snapshot = await uploadBytes(storageRef, uploadCroppedFile);
        log.dev("Uploaded file!", snapshot);

        const url = await getDownloadURL(snapshot.ref); // include token for cache busts.split("&")[0];

        log.dev("got url..", url);
        log.dev("sizes", getImageInfo(url));

        await onSelect(url);
        performanceMeasure.stop();
        uploadStateCallback(false);
        trackEvent("cover_uploaded");
      } catch (e) {
        console.error("Got invalid select file", e);
        uploadStateCallback(false);
        trackEvent("cover_error");
      }

      setImageSrc(null);
      setIsUploading(false);
      setShowCoverModal(false);
    },
    [onSelect, uid, uploadStateCallback, performanceMeasure],
  );

  const selectFile = React.useCallback(async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: "image/*",
    });

    try {
      // Only web compatible?
      const file = result.output?.[0];

      if (!file) {
        log.dev("No file output found");
        return;
      }
      const imageDataUrl = await readFile(file);
      if (imageDataUrl) {
        setImageSrc(imageDataUrl as string);
      }
      setShowCoverModal(false); // Cropper is showing
    } catch (e) {
      console.error("Got invalid select file", e);

      trackEvent("cover_error");
    }
  }, []);

  return (
    <>
      <Button
        disabled={!uid}
        icon={ImagePlus}
        onPress={() => {
          setShowCoverModal(true);
        }}
      >
        Cover Image
      </Button>

      {imageSrc && !isUploading && (
        <CropImage
          image={imageSrc}
          aspect={3 / 1}
          showGrid={true}
          cropShape={"rect"}
          onClose={() => {
            setShowCoverModal(false);
            setImageSrc(null);
            setIsUploading(false);
          }}
          onCropDone={async (f: Blob | null) => {
            if (f) {
              await uploadFile(f);
            }
          }}
        />
      )}

      <CoverModal
        visible={showCoverModal}
        onClose={() => {
          setShowCoverModal(false);
        }}
        onSelect={async (imgUrl: string) => {
          await onSelect(imgUrl);
        }}
        uploadButton={
          <Button
            type={"primary"}
            icon={ImagePlus}
            onPress={selectFile}
            isLoading={isUploading}
          >
            Upload cover photo
          </Button>
        }
      />
    </>
  );
}
